<template>
  <div
    class="hcl-form-input-radios"
    :class="computedCssRadios"
  >
    <slot>
      <template v-if="radios">
        <template
          v-for="radio in radios"
          :key="radio.id"
        >
          <hcl-form-input-radio
            v-bind="radio"
            :class="props.cssRadio"
            :width="props.width"
            :disabled="props.disabled ? 'true' : 'false'"
            :checked="radio.value === props.value ? 'true' : 'false'"
            @hcl-form-input-radio-changed="onFormInputRadioChanged"
          ></hcl-form-input-radio>
        </template>
      </template>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed } from 'vue'

import HclFormInputRadio from '@/components/FormInputRadio/FormInputRadio.vue'

interface Props {
  /**
   * ID of the Hcl Input Radios (optional).
   */
  id?: string
  /**
   * Array of Hcl Input Radio objects (optional)
   */
  radios: any[]
  /*
   * Orientation of the child radio elements, either "vertical" (default) or "horizontal"
   */
  orientation: 'vertical' | 'horizontal'
  /**
   * Value
   */
  value: string | number

  /**
   * CSS classes to add to the HCL Input Radios component, overrides the default 'pl-2'.
   */
  css?: string
  /**
   * CSS classes to add to the HCL Input Radio child components, overrides the default 'w-100'.
   */
  cssRadio?: string
  /**
   * Width of each Radio in %
   */
  width: number
  /**
   * Width of each Checkbox in %
   */
  disabled: boolean
}

const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  radios: undefined,
  orientation: 'vertical',
  value: undefined,
  css: 'pl-2',
  cssRadio: 'w-100',
  width: undefined,
  disabled: false
})

const pstate = reactive({
  radios: props.radios,
  orientation: props.orientation
})

const emit = defineEmits(['hcl-form-input-radios-changed'])

const onFormInputRadioChanged = (evt: any) => {
  emit('hcl-form-input-radios-changed', evt)
}

const computedCssRadios = computed(() => {
  const classes: string[] = []

  classes.push('orientation-' + pstate.orientation)
  classes.push(props.css)

  return classes.join(' ')
})
</script>

<style lang="scss" scoped>
.hcl-form-input-radios {
  min-height: calc(1.5em + 0.75rem + 2px);
  align-items: center;
  padding-left: 1rem;

  &.orientation-horizontal {
    display: flex;

    div {
      margin-right: 5px;

      &:last-of-type {
        margin-right: 0px;
      }
    }
  }
}
</style>
